/* ==========================================================================
   POS Billing screen

   A till-style companion to the Sales Invoice modal. Both write the same
   `InvSales` record with salesTransType 2, so a POS bill and a web invoice land
   in the same ledger and the same reports; only the entry-number series differs
   ("POS…" rather than "SI…") so a bill can be traced back to the terminal.

   Built for a touch terminal with no keyboard:
   - the keypad types into whichever field was touched last (product entry,
     quantity, price, discount, paid, split amounts, the charge popovers), and
     its keys never take focus, so the field stays live between presses;
   - Enter in product entry rings the item up and leaves the cursor there for
     the next one;
   - text (item notes, remarks, customer and item search) goes through the
     on-screen keyboard in the dialogs.

   Layout: one viewport, no page scroll. Only the bill lines scroll, inside
   their own card. The payment rail never scrolls: the keypad takes whatever
   height the rest of the rail leaves over.
   ========================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import {
  ShoppingCart,
  Settings,
  LogOut,
  Plus,
  Minus,
  Trash2,
  Search,
  Grid,
  Package,
  Tag,
  Percent,
  Circle,
  RotateCcw,
  Pause,
  Clock,
  FileText,
  CreditCard,
  DollarSign,
  Smartphone,
  Save,
  Printer,
  MoreVertical,
  Delete,
  Home,
  UserCheck,
  HelpCircle,
  X,
  CornerUpLeft,
  CornerDownLeft,
  User,
  Lock,
} from "feather-icons-react/build/IconComponents";

import POSField from "./POSField";
import {
  ItemLookupDialog,
  StockPriceCheckDialog,
  VariantPickDialog,
  ManualItemDialog,
  CustomerDialog,
  TextEntryDialog,
  RecentBillsDialog,
  HeldBillsDialog,
  ShortcutsDialog,
} from "./POSDialogs";
import {
  num,
  money,
  emptyLine,
  lineTotals,
  billTotals,
  isRealLine,
  nextEntryNumber,
  readHeldBills,
  writeHeldBills,
  printReceipt,
  isTypeable,
  editField,
  pressEnter,
  keepFocus,
  readPrefs,
  writePrefs,
} from "./posHelpers";

import { getWareHouseList } from "../../../services/MasterApiServices";
import { getSalesByTransType } from "../../../services/SalesApiServices";
import {
  getPosState,
  savePosBill,
  posError,
  readTillCounter,
  writeTillCounter,
} from "../../../services/PosApiServices";
import { getPrintSettingsByBranch } from "../../../services/AdminApiServices";
import { hasBarcode, barcodeIncludes } from "../../../core/utils/productBarcode";
import { findVariantByBarcode, lookupVariantBarcode, searchVariants, variantLabel, variantsOf } from "../variants";
import { fetchProduct, fetchCustomer, fetchTax, fetchUnit, fetchSettings } from "../../../redux/userSetting";
import { fetchBranchSettings } from "../../../redux/settingbyBranch";
import { fetchUserByBranch } from "../../../redux/userServices";
import { InvSales } from "../../../core/json/invSales";
import { FinancialYear } from "../../../core/json/FinancialYear";
import CustomerModal from "../../master/modal/CustomerModal";

import "../../../style/css/pos-screen.css";

const PAY_MODES = [
  { value: 0, key: "cash", label: "Cash", icon: <DollarSign /> },
  { value: 3, key: "card", label: "Card", icon: <CreditCard /> },
  { value: 2, key: "upi", label: "UPI", icon: <Smartphone /> },
  { value: 1, key: "credit", label: "Credit", icon: <FileText /> },
];

const ZERO_PAYMENTS = { cash: "", card: "", upi: "", credit: "" };

const FONT_LINK_ID = "pos-plex-font";
const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap";

const portalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 11000 }),
  menu: (base) => ({ ...base, zIndex: 11000 }),
};

/**
 * Select-all a field once it has focus, so the first key replaces the value.
 * The repeat on the next frame survives a tap's mouseup, which would otherwise
 * drop the selection - but only while nothing has been typed yet, or it would
 * select the first digit and let the second overwrite it.
 */
const selectAll = (event) => {
  const el = event.target;
  const valueAtFocus = el.value;
  el.select();
  window.requestAnimationFrame(() => {
    if (document.activeElement === el && el.value === valueAtFocus) el.select();
  });
};

/** Put focus back in a field the keypad was typing into, caret at the end. */
const refocusAtEnd = (el) => {
  el.focus();
  try {
    el.setSelectionRange(el.value.length, el.value.length);
  } catch {
    /* not every input type has a caret */
  }
};

const sameId = (a, b) => `${a ?? ""}`.toLowerCase() === `${b ?? ""}`.toLowerCase();

const SumLine = ({ label, value, tone }) => (
  <div className={`pos-sline ${tone ? `pos-sline--${tone}` : ""}`.trim()}>
    <span>{label}</span>
    <span className="pos-sline__lead" />
    <span className="pos-num">{value}</span>
  </div>
);

SumLine.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.string,
};

const POSBillingScreen = ({ onExit }) => {
  const dispatch = useDispatch();

  /* ---------------- redux ---------------- */
  const { branch } = useSelector((state) => state.branchlist);
  const { userId, userName } = useSelector((state) => state.userauth);
  const { productList, customerList, taxList, unitList, settingList } = useSelector((state) => state.usersetting);
  const { branchsettings } = useSelector((state) => state.settings);
  const sizeColour = !!branchsettings?.enableSizeColour;
  // UserServicelist is the signed-in user (the app header reads its name from
  // the same place); whoever is signed in is the cashier.
  const { UserListByBranch, UserServicelist } = useSelector((state) => state.userservice);
  const { companyInfolist } = useSelector((state) => state.companyInfo);

  /* ---------------- bill state ---------------- */
  const [lines, setLines] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [lastScanKey, setLastScanKey] = useState(null); // line shown under product entry
  const [entryNumber, setEntryNumber] = useState("");
  // The business date and this till's counter / open shift come from the POS
  // API: the date moves on only at day close (never at midnight), the shift
  // number at shift close. The server dates and shifts every bill it saves.
  const [posState, setPosState] = useState(null);
  const [counterID, setCounterID] = useState("");
  const businessDate = useMemo(
    () => (posState?.transactionDate ? dayjs(posState.transactionDate) : dayjs()),
    [posState]
  );
  const tillCounter = useMemo(
    () => (posState?.counters || []).find((c) => sameId(c.counterID, counterID)) || null,
    [posState, counterID]
  );
  const [customer, setCustomer] = useState(null);
  const [mobile, setMobile] = useState("");
  const [warehouse, setWarehouse] = useState(null);
  const [salesman, setSalesman] = useState(null);
  const [remarks, setRemarks] = useState("");
  // Charges are kept as the typed text so "1." survives until the next digit.
  const [extraCharge, setExtraCharge] = useState("");
  const [billDiscount, setBillDiscount] = useState("");
  const [roundOff, setRoundOff] = useState("");

  /* ---------------- payment state ---------------- */
  const [paid, setPaid] = useState("");
  const [payMode, setPayMode] = useState(0);
  const [splitPay, setSplitPay] = useState(false);
  const [payments, setPayments] = useState(ZERO_PAYMENTS);

  /* ---------------- ui state ---------------- */
  const [scan, setScan] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // lookup | quick | stock | manual | customer | note | remarks | recent | held | keys
  const [lookupTerm, setLookupTerm] = useState("");
  const [variantPick, setVariantPick] = useState(null); // { product, quantity } - the size / colour grid
  const [noteLine, setNoteLine] = useState(null);
  const [popover, setPopover] = useState(null); // { name, top, left, below }
  const [customerModal, setCustomerModal] = useState(false);
  const [saving, setSaving] = useState(false);
  // `saving` disables the buttons only after a re-render, and F9/F10 ignore
  // it; this blocks a second save while one is on its way.
  const savingRef = useRef(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [clock, setClock] = useState(dayjs());
  const [prefs, setPrefs] = useState(readPrefs);
  const [focusedField, setFocusedField] = useState("scan");
  const [warehouses, setWarehouses] = useState([]);
  const [printSettings, setPrintSettings] = useState(null);
  const [recentBills, setRecentBills] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [heldBills, setHeldBills] = useState([]);

  const rootRef = useRef(null);
  const scanRef = useRef(null);
  const paidRef = useRef(null);
  const lastFieldRef = useRef(null);

  // With touch mode on, fields ask the device not to raise its own keyboard -
  // the screen's keypad and keyboard do the typing.
  const im = prefs.touchMode ? "none" : undefined;

  /* ---------------- derived settings ---------------- */

  const decimals = useMemo(() => {
    const match = settingList?.currencyFormat?.toString()?.match(/\.0+/);
    return match ? match[0].length - 1 : 2;
  }, [settingList]);

  // Tax columns are hidden only when the branch explicitly disables tax in
  // sales; an unloaded setting must not silently drop tax from a bill, and a
  // line that does carry VAT always shows it, whatever the setting says.
  const showTax =
    branchsettings?.disableTaxInSale !== true || lines.some((line) => num(line.taxPercent) > 0);

  const fmt = useCallback((value) => money(value, decimals), [decimals]);

  const taxOf = useCallback(
    (taxGuid) => num((taxList || []).find((tax) => tax.guid === taxGuid)?.percentage),
    [taxList]
  );

  const updatePref = (key, value) =>
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      writePrefs(next);
      return next;
    });

  /* ---------------- option lists ---------------- */

  const productOptions = useMemo(
    () =>
      (productList || []).map((product) => ({
        value: product.guid,
        label: product.name,
        code: product.code,
        barcode: product.barcode,
        product,
      })),
    [productList]
  );

  const customerOptions = useMemo(
    () => (customerList || []).map((item) => ({ value: item.guid, label: item.name, customer: item })),
    [customerList]
  );

  const warehouseOptions = useMemo(
    () => (warehouses || []).map((item) => ({ value: item.guid, label: item.name, warehouse: item })),
    [warehouses]
  );

  const salesmanOptions = useMemo(
    () => (UserListByBranch || []).map((user) => ({ value: user.guid, label: user.name })),
    [UserListByBranch]
  );

  const productFilter = (option, rawInput) => {
    const needle = `${rawInput ?? ""}`.trim().toLowerCase();
    if (!needle) return true;
    const { label, code, barcode } = option.data || {};
    return (
      `${label ?? ""}`.toLowerCase().includes(needle) ||
      `${code ?? ""}`.toLowerCase().includes(needle) ||
      `${barcode ?? ""}`.toLowerCase().includes(needle)
    );
  };

  /* ---------------- totals ---------------- */

  const totals = useMemo(
    () => billTotals(lines, { extraCharge, billDiscount, roundOff, decimals }),
    [lines, extraCharge, billDiscount, roundOff, decimals]
  );

  // "VAT 5%" when every taxed line shares a rate, plain "VAT" when they differ.
  const taxLabel = useMemo(() => {
    const rates = new Set(lines.filter((line) => num(line.taxPercent) > 0).map((line) => num(line.taxPercent)));
    return rates.size === 1 ? `VAT ${[...rates][0]}%` : "VAT";
  }, [lines]);

  const splitTotal = useMemo(
    () => Object.values(payments).reduce((total, value) => total + num(value), 0),
    [payments]
  );

  const isCreditOnly = !splitPay && payMode === 1;
  const paidTotal = splitPay ? splitTotal : isCreditOnly ? totals.net : num(paid);
  const change = paidTotal - totals.net;
  const activeLine = lines.find((line) => line.key === activeKey) || null;
  const lastScanLine = lines.find((line) => line.key === lastScanKey) || null;
  const cashierName = UserServicelist?.name || userName || "—";
  const unitsLabel = Number.isInteger(totals.units) ? totals.units : totals.units.toFixed(2);

  // Tender suggestions built from the amount due, so they suit any bill.
  const quickCash = useMemo(() => {
    const due = Math.round(totals.net * 100) / 100;
    if (due <= 0) return [];
    const ceilTo = (step) => Math.ceil(due / step) * step;
    const values = [due, ceilTo(10), ceilTo(50), ceilTo(100), ceilTo(500)];
    return [...new Set(values)].filter((value) => value >= due).slice(0, 5);
  }, [totals.net]);

  /* ---------------- data loading ---------------- */

  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (!branch?.guid) return;
    dispatch(fetchProduct(branch));
    dispatch(fetchCustomer(branch));
    dispatch(fetchTax());
    dispatch(fetchUnit());
    dispatch(fetchSettings(branch));
    dispatch(fetchBranchSettings(branch.guid));
    dispatch(fetchUserByBranch(branch));
  }, [dispatch, branch]);

  useEffect(() => {
    if (!branch?.guid) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const response = await getWareHouseList(branch?.guid);
        if (cancelled) return;
        setWarehouses(Array.isArray(response) ? response.filter((item) => item.branchID === branch.guid) : []);
      } catch (error) {
        console.error("Failed to fetch warehouses", error);
      }

      try {
        const settings = await getPrintSettingsByBranch(branch.guid);
        if (!cancelled) setPrintSettings(settings);
      } catch (error) {
        console.error("Failed to fetch print settings", error);
      }
    })();

    setHeldBills(readHeldBills(branch.guid));
    return () => {
      cancelled = true;
    };
  }, [branch]);

  // Default warehouse from branch settings, once both have arrived.
  useEffect(() => {
    if (warehouse || !warehouses.length) return;
    const preferred = warehouses.find((item) => item.guid === branchsettings?.defaultWareHouseID);
    const chosen = preferred || warehouses[0];
    if (chosen) setWarehouse({ value: chosen.guid, label: chosen.name, warehouse: chosen });
  }, [warehouses, branchsettings, warehouse]);

  const loadEntryNumber = useCallback(async () => {
    if (!branch?.guid) return;
    try {
      const response = await getSalesByTransType(branch, 2);
      setEntryNumber(nextEntryNumber(response?.data, "POS"));
    } catch (error) {
      console.error("Failed to read the bill series", error);
      setEntryNumber("POS1");
    }
  }, [branch]);

  useEffect(() => {
    loadEntryNumber();
  }, [loadEntryNumber]);

  // Which counter this till bills on belongs to the device. The cashier picks
  // it once (again from the status strip); a branch with one counter needs no pick.
  const pickCounter = useCallback(
    async (state) => {
      const counters = state?.counters || [];
      if (!counters.length) {
        toast.error("This branch has no counter. Add one in Masters > Counter before billing.");
        return;
      }
      const { value } = await Swal.fire({
        title: "Which counter is this till?",
        input: "select",
        inputOptions: Object.fromEntries(counters.map((c) => [c.counterID, c.counterName])),
        inputValue: counterID || counters[0].counterID,
        showCancelButton: Boolean(counterID),
        allowOutsideClick: Boolean(counterID),
        confirmButtonColor: "#4B44D6",
        confirmButtonText: "Use this counter",
      });
      if (!value) return;
      writeTillCounter(branch?.guid, value);
      setCounterID(value);
    },
    [branch, counterID]
  );

  const loadPosState = useCallback(async () => {
    if (!branch?.guid) return null;
    try {
      const state = await getPosState(branch.guid);
      setPosState(state);
      return state;
    } catch (error) {
      toast.error(posError(error, "Could not read the POS business date"));
      return null;
    }
  }, [branch]);

  useEffect(() => {
    if (!branch?.guid) return;
    (async () => {
      const state = await loadPosState();
      if (!state) return;
      const counters = state.counters || [];
      const known = counters.find((c) => sameId(c.counterID, readTillCounter(branch.guid)));
      if (known) setCounterID(known.counterID);
      else if (counters.length === 1) {
        writeTillCounter(branch.guid, counters[0].counterID);
        setCounterID(counters[0].counterID);
      } else pickCounter(state);

      const date = dayjs(state.transactionDate);
      if (date.isBefore(dayjs(), "day")) {
        toast(`The business date is still ${date.format("DD MMM YYYY")}. Run Day Close once that day's trading is done.`, {
          icon: "⚠️",
          duration: 7000,
        });
      }
    })();
    // the pick runs once per branch; later picks come from the status strip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, loadPosState]);

  // Tax rates arrive after the first render. Re-resolve any line rung up before
  // the list landed, so a fast first scan is never quietly untaxed.
  useEffect(() => {
    if (!taxList?.length) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (!line.taxGuid) return line;
        const percent = taxOf(line.taxGuid);
        if (percent === num(line.taxPercent)) return line;
        changed = true;
        return { ...line, taxPercent: percent };
      });
      return changed ? next : prev;
    });
  }, [taxList, taxOf]);

  // Wall clock in the status strip, and the online dot.
  useEffect(() => {
    const timer = setInterval(() => setClock(dayjs()), 20000);
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  /* ---------------- focus ---------------- */

  const focusScan = useCallback(() => {
    setTimeout(() => scanRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    focusScan();
  }, [focusScan]);

  // Remember the last field touched, so the keypad keeps typing into it, and
  // light up the keypad key that belongs to it.
  const handleFocusIn = (event) => {
    const el = event.target;
    if (!isTypeable(el) || !rootRef.current?.contains(el)) return;
    lastFieldRef.current = el;
    const field = el.dataset?.posField || "";
    const line = el.dataset?.line || "";
    setFocusedField(field ? `${field}${line ? `:${line}` : ""}` : "");
    if (line) setActiveKey(line);
  };

  // The app registers a global Enter handler that jumps focus to the next
  // field. On a till that would throw the cursor out of product entry after
  // every scan, so Enter stops here. The customer form keeps its behaviour.
  const containEnter = (event) => {
    if (event.key === "Enter" && !event.target.closest?.(".modal")) event.stopPropagation();
  };

  // A tap on nothing in particular returns the cursor to product entry, so the
  // scanner is always ready.
  const refocusOnNeutralTap = (event) => {
    if (dialog || variantPick || popover || panelOpen || customerModal) return;
    const interactive =
      "input, textarea, select, button, a, label, .pos-select, .pos-pop, .pos-panel, .pos-overlay, .modal";
    if (event.target.closest?.(interactive)) return;
    focusScan();
  };

  const enterToScan = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      focusScan();
    }
  };

  /* ---------------- line editing ---------------- */

  const addProduct = useCallback(
    (product, quantity = 1) => {
      if (!product) return;
      // a variant (size + colour) rings up as its own line, under its own name / price
      const variant = product.__variant || null;
      setLines((prev) => {
        // The same item twice is one line with a bigger quantity.
        const existing = prev.findIndex(
          (line) =>
            line.product === product.guid && !line.manual && (line.variantID || null) === (variant?.variantID || null)
        );
        if (existing !== -1) {
          const next = [...prev];
          next[existing] = { ...next[existing], quantity: num(next[existing].quantity) + quantity };
          setActiveKey(next[existing].key);
          setLastScanKey(next[existing].key);
          return next;
        }

        const line = {
          ...emptyLine(),
          product: product.guid,
          name: product.name,
          code: product.code || "",
          barcode: product.barcode || "",
          unit: product?.unit?.guid ?? product?.unit ?? null,
          unitName: product?.unit?.name ?? "",
          taxGuid: product?.taxID ?? null,
          taxPercent: taxOf(product?.taxID),
          quantity,
          price: num(product?.retailRate ?? product?.basePrice ?? product?.cost),
          mrp: num(product?.mrp),
          variantID: variant?.variantID || null,
          variantLabel: variant ? variantLabel(variant) : "",
        };
        setActiveKey(line.key);
        setLastScanKey(line.key);
        return [...prev, line];
      });
    },
    [taxOf]
  );

  // An item with sizes and colours opens the size / colour grid (stock per
  // cell) instead of ringing up the bare product; anything else goes straight on.
  const pickProduct = useCallback(
    (product, quantity = 1) => {
      if (!product) return;
      if (sizeColour && !product.__variant && variantsOf(product).length) setVariantPick({ product, quantity });
      else addProduct(product, quantity);
    },
    [sizeColour, addProduct]
  );

  const updateLine = useCallback((key, patch) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }, []);

  const removeLine = useCallback((key) => {
    setLines((prev) => prev.filter((line) => line.key !== key));
    setActiveKey((prev) => (prev === key ? null : prev));
  }, []);

  // The stepper stops at one; the bin removes a line. A mis-tap on minus should
  // never make an item vanish.
  const stepQuantity = (line, delta) => {
    const next = num(line.quantity) + delta;
    if (next > 0) updateLine(line.key, { quantity: Math.round(next * 1000) / 1000 });
  };

  /* ---------------- product entry ---------------- */

  const handleScan = (raw) => {
    const scanned = `${raw ?? ""}`.trim();
    if (!scanned) return;

    // "3*CODE" or "3xCODE" rings three of them, the usual till shorthand.
    const shorthand = scanned.match(/^(\d+(?:\.\d+)?)\s*[*x]\s*(.+)$/i);
    const quantity = shorthand ? num(shorthand[1], 1) : 1;
    const needle = (shorthand ? shorthand[2] : scanned).trim().toLowerCase();

    // A scanner sends an exact code, so that wins outright - a variant's
    // barcode / SKU first, then the product's own.
    const exact =
      (sizeColour && findVariantByBarcode(productList, needle)) ||
      (productList || []).find(
        (product) =>
          hasBarcode(product, needle) ||
          `${product?.code ?? ""}`.trim().toLowerCase() === needle
      );
    if (exact) {
      pickProduct(exact, quantity);
      setScan("");
      return;
    }

    // Typed text falls back to a name search: one hit rings it up, several open
    // the look-up already filtered, none is an error.
    const partial = (productList || []).filter(
      (product) =>
        `${product?.name ?? ""}`.toLowerCase().includes(needle) ||
        `${product?.code ?? ""}`.toLowerCase().includes(needle) ||
        barcodeIncludes(product, needle)
    );
    const partialVariants = sizeColour && !partial.length ? searchVariants(productList, needle, 2) : [];
    if (partial.length === 1 || partialVariants.length === 1) {
      pickProduct(partial[0] || partialVariants[0], quantity);
      setScan("");
      return;
    }
    if (partial.length > 1) {
      setLookupTerm(needle);
      setDialog("lookup");
      setScan("");
      return;
    }

    // a barcode the product list doesn't know - ask the server
    if (sizeColour) {
      lookupVariantBarcode(productList, needle).then((p) => {
        if (!p) return toast.error(`No item found for “${needle}”`);
        pickProduct(p, quantity);
        setScan("");
      });
      return;
    }
    toast.error(`No item found for “${needle}”`);
  };

  /* ---------------- keypad ---------------- */

  // Where a key press lands: the focused field if there is one, else the last
  // field touched, else product entry.
  const keypadTarget = () => {
    const active = document.activeElement;
    if (isTypeable(active) && rootRef.current?.contains(active)) return active;
    // Re-entering a field the keypad was already typing into carries on from
    // the end, rather than select-all-and-replace.
    const last = lastFieldRef.current;
    if (last && last.isConnected && isTypeable(last)) {
      refocusAtEnd(last);
      return last;
    }
    if (scanRef.current) refocusAtEnd(scanRef.current);
    return scanRef.current;
  };

  const pressKey = (token) => {
    const el = keypadTarget();
    if (!el) return;
    if (token === "back") editField(el, "backspace");
    else if (token === "clear") editField(el, "clear");
    else if (token === "enter") pressEnter(el);
    else editField(el, "insert", token);
  };

  // Qty / Price / Disc keys move the cursor into that field of the selected
  // line (the last line if none is selected), value selected, ready to type.
  const focusLineField = (kind) => {
    const line = activeLine || lines[lines.length - 1];
    if (!line) {
      toast.error("Add an item first");
      return;
    }
    setActiveKey(line.key);
    const el = rootRef.current?.querySelector(`[data-pos-field="${kind}"][data-line="${line.key}"]`);
    if (el) {
      el.focus();
      el.select();
    }
  };

  const focusPaid = useCallback(() => {
    const el = splitPay
      ? rootRef.current?.querySelector('[data-pos-field="pay-cash"]')
      : isCreditOnly
      ? null
      : paidRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  }, [splitPay, isCreditOnly]);

  const fieldIs = (kind) => focusedField === kind || focusedField.startsWith(`${kind}:`);

  /* ---------------- payments ---------------- */

  // Tapping a method in split mode fills it with whatever is still unpaid.
  const fillRemaining = (key) => {
    const others = Object.entries(payments).reduce((total, [name, value]) => (name === key ? total : total + num(value)), 0);
    const remaining = Math.max(0, totals.net - others);
    setPayments((prev) => ({ ...prev, [key]: remaining ? remaining.toFixed(decimals) : "" }));
  };

  const applyQuickCash = (value) => {
    if (splitPay) setPayments((prev) => ({ ...prev, cash: `${value}` }));
    else setPaid(`${value}`);
  };

  /* ---------------- bill actions ---------------- */

  const resetBill = useCallback(
    (silent = false) => {
      setLines([]);
      setActiveKey(null);
      setLastScanKey(null);
      setCustomer(null);
      setMobile("");
      setSalesman(null);
      setRemarks("");
      setExtraCharge("");
      setBillDiscount("");
      setRoundOff("");
      setPaid("");
      setPayments(ZERO_PAYMENTS);
      setSplitPay(false);
      setPayMode(0);
      setScan("");
      loadEntryNumber();
      if (!silent) focusScan();
    },
    [loadEntryNumber, focusScan]
  );

  const holdBill = useCallback(() => {
    if (!lines.length) {
      toast.error("There is nothing to hold");
      return;
    }
    const bill = {
      id: `${Date.now()}`,
      heldAt: new Date().toISOString(),
      customerName: customer?.label || "",
      customer,
      mobile,
      salesman,
      remarks,
      extraCharge,
      billDiscount,
      roundOff,
      lines,
    };
    const next = [bill, ...readHeldBills(branch?.guid)].slice(0, 50);
    writeHeldBills(branch?.guid, next);
    setHeldBills(next);
    resetBill(true);
    focusScan();
    toast.success("Bill held");
  }, [lines, customer, mobile, salesman, remarks, extraCharge, billDiscount, roundOff, branch, resetBill, focusScan]);

  const recallBill = (bill) => {
    setLines(bill.lines || []);
    setCustomer(bill.customer || null);
    setMobile(bill.mobile || "");
    setSalesman(bill.salesman || null);
    setRemarks(bill.remarks ?? bill.notes ?? "");
    setExtraCharge(`${bill.extraCharge ?? ""}`);
    setBillDiscount(`${bill.billDiscount ?? ""}`);
    setRoundOff(`${bill.roundOff ?? ""}`);
    setActiveKey(null);
    setLastScanKey(null);

    const next = readHeldBills(branch?.guid).filter((item) => item.id !== bill.id);
    writeHeldBills(branch?.guid, next);
    setHeldBills(next);
    setDialog(null);
    focusScan();
  };

  const deleteHeldBill = (id) => {
    const next = readHeldBills(branch?.guid).filter((item) => item.id !== id);
    writeHeldBills(branch?.guid, next);
    setHeldBills(next);
  };

  const openRecentBills = async () => {
    setDialog("recent");
    setRecentLoading(true);
    try {
      const response = await getSalesByTransType(branch, 2);
      const list = Array.isArray(response?.data) ? response.data : [];
      setRecentBills([...list].sort((a, b) => dayjs(b.invdate).valueOf() - dayjs(a.invdate).valueOf()).slice(0, 40));
    } catch (error) {
      toast.error("Could not load recent bills");
    } finally {
      setRecentLoading(false);
    }
  };

  const closeDialog = () => {
    setDialog(null);
    setNoteLine(null);
    setLookupTerm("");
    focusScan();
  };

  /* ---------------- printing ---------------- */

  const receiptHeader = useMemo(() => {
    const settings = printSettings?.[0] ?? {};
    return {
      companyName: settings.companyName || companyInfolist?.[0]?.name || branch?.name || "",
      header1: settings.header1 || companyInfolist?.[0]?.address1,
      header2: settings.header2 || companyInfolist?.[0]?.address2,
      header3: settings.header3,
      header4: settings.header4,
      header5: settings.header5,
      footerText1: settings.footerText1,
      footerText2: settings.footerText2,
      qr: {
        branchID: branch?.guid,
        enabled: branchsettings?.generateQrCodeInBillPrint === true,
        sellerName: settings.companyName || companyInfolist?.[0]?.name || "",
        vatNumber: companyInfolist?.[0]?.tin || "",
      },
    };
  }, [printSettings, companyInfolist, branch, branchsettings]);

  const printCurrentBill = useCallback(
    (billNumber, stamp, tendered = paidTotal) => {
      printReceipt(
        {
          header: receiptHeader,
          entryNumber: billNumber || entryNumber,
          invoiceDate: (stamp || dayjs()).format("DD MMM YYYY, hh:mm A"),
          customerName: customer?.label,
          cashier: cashierName,
          salesman: salesman?.label,
          lines: lines.filter(isRealLine),
          totals,
          payments: PAY_MODES.map((mode) => ({
            label: mode.label,
            amount: splitPay ? payments[mode.key] : payMode === mode.value ? totals.net : 0,
          })),
          paid: tendered,
          change: Math.max(0, tendered - totals.net),
          notes: remarks,
          decimals,
          taxLabel,
        },
        stamp
      );
    },
    [receiptHeader, entryNumber, customer, cashierName, salesman, lines, totals, splitPay, payments, payMode, paidTotal, remarks, decimals, taxLabel]
  );

  const printSavedBill = (bill) => {
    const billLines = (bill?.invSalesDetails || []).map((detail) => ({
      name: detail?.product?.name || detail?.description || "Item",
      quantity: num(detail?.quantity),
      price: num(detail?.price),
      discountPercent: num(detail?.discPercentage),
      taxPercent: num(detail?.taxPercentage ?? detail?.TaxPercentage),
      notes: detail?.notes || "",
    }));

    printReceipt(
      {
        header: receiptHeader,
        entryNumber: bill?.entrynumber,
        invoiceDate: bill?.invdate ? dayjs(bill.invdate).format("DD MMM YYYY, hh:mm A") : "",
        customerName: bill?.customerName || bill?.customer?.name,
        lines: billLines,
        totals: {
          gross: num(bill?.grossTotal),
          lineDiscount: num(bill?.discountTotal) - num(bill?.discAmt),
          billDiscount: num(bill?.discAmt),
          extraCharge: num(bill?.additionalAmt),
          tax: num(bill?.taxTotal),
          roundOff: num(bill?.roundOff),
          net: num(bill?.netTotal),
          units: billLines.reduce((total, line) => total + num(line.quantity), 0),
        },
        payments: [
          { label: "Cash", amount: bill?.cash },
          { label: "Card", amount: bill?.card },
          { label: "UPI", amount: bill?.upi },
          { label: "Credit", amount: bill?.bank },
        ],
        paid: num(bill?.paid),
        change: num(bill?.change),
        notes: bill?.terms || "",
        decimals,
      },
      bill?.invdate
    );
  };

  /* ---------------- saving ---------------- */

  const saveBill = useCallback(
    async (alsoPrint) => {
      if (savingRef.current) return;
      const billLines = lines.filter(isRealLine);

      if (!billLines.length) {
        toast.error("Add at least one item before saving");
        return;
      }
      if (!branch?.guid) {
        toast.error("No branch is selected");
        return;
      }
      if (!counterID) {
        toast.error("Pick this till's counter before billing");
        pickCounter(posState);
        return;
      }
      // Saving with nothing typed in Paid means the customer handed over the
      // exact amount - the usual till behaviour. Without this a plain SAVE on a
      // cash bill was always "short by" the whole total and never posted.
      const tendered = !splitPay && !isCreditOnly && `${paid}`.trim() === "" ? totals.net : paidTotal;
      if (tendered < totals.net - 0.005) {
        toast.error(`Payment is short by ${fmt(totals.net - tendered)}`);
        return;
      }

      // The API needs a customer on every sales invoice (the web invoice makes
      // it mandatory, and editing a bill reads customer.guid). A walk-in bill
      // is filed under the branch's default customer from Settings > General.
      const customerRecord =
        (customerList || []).find((item) => item.guid === customer?.value) ||
        (customerList || []).find((item) => item.guid === branchsettings?.defaultCustomerID) ||
        null;
      if (!customerRecord) {
        toast.error("Pick a customer for this bill, or set a default customer in Settings > General for walk-in sales");
        setDialog("customer");
        return;
      }

      const amounts = splitPay
        ? { cash: num(payments.cash), card: num(payments.card), upi: num(payments.upi), credit: num(payments.credit) }
        : {
            cash: payMode === 0 ? totals.net : 0,
            credit: payMode === 1 ? totals.net : 0,
            upi: payMode === 2 ? totals.net : 0,
            card: payMode === 3 ? totals.net : 0,
          };

      // The mode that carried most of the bill is the one the record is filed
      // under, which is all `salesPaymentType` can express.
      const dominant = PAY_MODES.reduce((best, mode) => (amounts[mode.key] > amounts[best.key] ? mode : best));

      // Business date, wall-clock time.
      const now = dayjs();
      const stamp = businessDate.hour(now.hour()).minute(now.minute()).second(now.second());

      const objsales = new InvSales();
      const objfinance = new FinancialYear();

      objsales.counterID = counterID;
      objsales.entrynumber = entryNumber;
      objsales.branch = branch;
      objsales.invdate = stamp.format("YYYY-MM-DDTHH:mm:ss");
      objsales.deliveryDate = stamp.format("YYYY-MM-DDTHH:mm:ss");
      objsales.salesTransType = 2;
      objsales.salesPaymentType = dominant.value;
      objsales.salesMode = 0;
      objsales.reftype = 0;
      objsales.refNo = "";
      objsales.currency = "00000000-0000-0000-0000-000000000000";
      objsales.customer = customerRecord;
      objsales.customerName = customer?.label || customerRecord.name || "Walk-in Customer";
      objsales.customerContactno = mobile || "";
      objsales.salesman = salesman?.value || null;
      objsales.location = warehouse?.warehouse || null;
      // The invoice screen labels `terms` "Remarks"; the POS writes the same field.
      objsales.terms = remarks || "";
      objsales.dispatch = "";
      objsales.dispatchTrough = "";
      objsales.createdUser = `${userId?.id ?? ""}`;
      objsales.updatedUser = `${userId?.id ?? ""}`;

      objsales.additionalAmt = totals.extraCharge;
      objsales.discAmt = totals.billDiscount;
      objsales.roundOff = totals.roundOff;
      objsales.grossTotal = totals.gross;
      objsales.discountTotal = totals.discountTotal;
      objsales.taxTotal = totals.tax;
      objsales.netTotal = totals.net;
      objsales.paid = tendered;
      objsales.change = Math.max(0, tendered - totals.net);

      objsales.cash = amounts.cash;
      objsales.card = amounts.card;
      objsales.upi = amounts.upi;
      // Credit rides in `bank`, the same column the invoice modal writes it to.
      objsales.bank = amounts.credit;

      objfinance.id = branchsettings?.defaultFinancialYearID;
      objsales.financialYear = objfinance;
      objsales.Company = companyInfolist?.[0];

      // line amounts from the bill's totals: each line's VAT is its part of its
      // rate's VAT after the bill discount / extra charge (the ZATCA way)
      objsales.invSalesDetails = billLines.map((line) => {
        const t = totals.lines[lines.indexOf(line)] || lineTotals(line);
        return {
          product: (productList || []).find((item) => item.guid === line.product) || null,
          unit: (unitList || []).find((item) => item.guid === line.unit) || null,
          itemCode: line.code || "",
          description: line.name || "",
          batch: line.batch || "",
          expiryDate: stamp.format("YYYY-MM-DDTHH:mm:ss"),
          quantity: num(line.quantity),
          price: num(line.price),
          mrp: num(line.mrp),
          foc: num(line.foc),
          grossAmount: t.gross,
          discPercentage: num(line.discountPercent),
          discAmt: t.discountAmount,
          taxPercentage: num(line.taxPercent),
          taxamnt: t.taxAmount,
          netAmt: t.net,
          notes: line.notes || "",
          variantID: line.variantID || null,
        };
      });

      savingRef.current = true;
      setSaving(true);
      try {
        // the server numbers the bill (the number shown is a preview), dates it
        // on the business date and files it under this counter's open shift
        const saved = await savePosBill(objsales);
        const billNo = saved?.entryNumber || entryNumber;

        toast.success(`Bill ${billNo} saved${saved?.shiftNo ? ` · shift ${saved.shiftNo}` : ""}`);
        if (alsoPrint || prefs.printOnSave) printCurrentBill(billNo, stamp, tendered);
        resetBill(true);
        loadPosState();
        focusScan();
      } catch (error) {
        toast.error(posError(error, "Could not save the bill"));
        console.error("Failed to save POS bill", error);
        // the day was closed from another screen: pick up the new date
        if (error?.response?.status === 409) loadPosState();
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [
      lines,
      branch,
      paid,
      paidTotal,
      isCreditOnly,
      totals,
      fmt,
      splitPay,
      payments,
      payMode,
      businessDate,
      entryNumber,
      customerList,
      customer,
      mobile,
      salesman,
      warehouse,
      remarks,
      userId,
      change,
      branchsettings,
      companyInfolist,
      productList,
      unitList,
      prefs.printOnSave,
      printCurrentBill,
      resetBill,
      focusScan,
      counterID,
      posState,
      pickCounter,
      loadPosState,
    ]
  );

  const confirmReset = () => {
    if (!lines.length) {
      resetBill();
      return;
    }
    Swal.fire({
      title: "Clear this bill?",
      text: "Every line on the current bill will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4B44D6",
      cancelButtonColor: "#67728A",
      confirmButtonText: "Clear it",
    }).then((result) => {
      if (result.isConfirmed) resetBill();
    });
  };

  /* ---------------- physical keyboard shortcuts ---------------- */

  useEffect(() => {
    const onKeyDown = (event) => {
      if (dialog || variantPick) return; // a dialog owns the keyboard while it is open

      const key = event.key;
      if (key === "Escape") {
        setPopover(null);
        setPanelOpen(false);
        return;
      }
      const fkeys = {
        F1: () => setDialog("keys"),
        F2: focusPaid,
        F3: focusScan,
        F4: () => setDialog("lookup"),
        F6: () => setDialog("quick"),
        F7: holdBill,
        F8: () => setDialog("held"),
        F9: () => saveBill(false),
        F10: () => saveBill(true),
      };
      if (fkeys[key]) {
        event.preventDefault();
        fkeys[key]();
        return;
      }

      const typing = isTypeable(document.activeElement);
      if (key === "Delete" && activeKey && !typing) {
        event.preventDefault();
        removeLine(activeKey);
      } else if ((key === "ArrowDown" || key === "ArrowUp") && lines.length && !typing) {
        event.preventDefault();
        const index = lines.findIndex((line) => line.key === activeKey);
        const nextIndex =
          key === "ArrowDown" ? Math.min(lines.length - 1, index + 1) : Math.max(0, (index === -1 ? lines.length : index) - 1);
        setActiveKey(lines[nextIndex]?.key ?? null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeKey, lines, dialog, variantPick, holdBill, saveBill, removeLine, focusScan, focusPaid]);

  /* ---------------- popovers and the details panel ---------------- */

  const openPopover = (name, below = false) => (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - 292);
    const top = below ? rect.bottom + 8 : rect.top - 8;
    setPopover((prev) => (prev?.name === name ? null : { name, left, top, below }));
  };

  const closePopover = () => {
    setPopover(null);
    focusScan();
  };

  useEffect(() => {
    if (!popover) return undefined;
    const onDown = (event) => {
      // The keypad types into the popover, so a keypad tap must not close it.
      if (!event.target.closest?.(".pos-pop, [data-pop-anchor], [data-keep-pop]")) setPopover(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [popover]);

  // Open a popover with its field focused and selected, so the keypad writes there.
  useEffect(() => {
    if (!popover || popover.name === "settings") return;
    const el = rootRef.current?.querySelector(".pos-pop input[data-numeric]");
    if (el) {
      el.focus();
      el.select();
    }
  }, [popover]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onDown = (event) => {
      if (!event.target.closest?.('.pos-panel, [data-panel-anchor], [class*="pos-select__menu"]')) setPanelOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [panelOpen]);

  const popStyle = popover
    ? { left: popover.left, top: popover.top, transform: popover.below ? "none" : "translateY(-100%)" }
    : undefined;

  /* ---------------- render helpers ---------------- */

  const numericProps = (field, line) => ({
    type: "text",
    inputMode: im,
    autoComplete: "off",
    "data-numeric": "",
    "data-pos-field": field,
    ...(line ? { "data-line": line } : {}),
    onFocus: selectAll,
  });

  const renderLineRow = (line, index) => {
    const t = totals.lines[lines.indexOf(line)] || lineTotals(line);
    const meta = [line.barcode || line.code, line.unitName].filter(Boolean).join(" · ");
    return (
      <tr key={line.key} className={line.key === activeKey ? "is-active" : ""} onMouseDown={() => setActiveKey(line.key)}>
        <td className="pos-idx">{index + 1}</td>
        <td>
          <div className="pos-desc">
            <span className="pos-desc__name">{line.name}</span>
            <span className="pos-desc__meta">
              {meta || "—"}
              {line.notes ? <em> · {line.notes}</em> : null}
            </span>
          </div>
        </td>
        <td>
          <div className="pos-step">
            <button type="button" tabIndex={-1} onMouseDown={keepFocus} onClick={() => stepQuantity(line, -1)} aria-label={`Less ${line.name}`}>
              <Minus />
            </button>
            <input
              {...numericProps("qty", line.key)}
              value={line.quantity}
              onChange={(event) => updateLine(line.key, { quantity: event.target.value })}
              onKeyDown={enterToScan}
              aria-label={`Quantity of ${line.name}`}
            />
            <button type="button" tabIndex={-1} onMouseDown={keepFocus} onClick={() => stepQuantity(line, 1)} aria-label={`More ${line.name}`}>
              <Plus />
            </button>
          </div>
        </td>
        <td>
          <input
            {...numericProps("price", line.key)}
            className="pos-cellinput"
            value={line.price}
            onChange={(event) => updateLine(line.key, { price: event.target.value })}
            onKeyDown={enterToScan}
            aria-label={`Price of ${line.name}`}
          />
        </td>
        <td>
          <input
            {...numericProps("disc", line.key)}
            className="pos-cellinput"
            value={line.discountPercent}
            onChange={(event) => updateLine(line.key, { discountPercent: event.target.value })}
            onKeyDown={enterToScan}
            aria-label={`Discount on ${line.name}`}
          />
        </td>
        {showTax ? (
          <td>
            <select
              className="pos-cellinput pos-cellinput--select"
              value={line.taxGuid ?? ""}
              onChange={(event) => {
                const guid = event.target.value || null;
                updateLine(line.key, { taxGuid: guid, taxPercent: taxOf(guid) });
              }}
              aria-label={`Tax on ${line.name}`}
            >
              <option value="">0.00</option>
              {(taxList || []).map((tax) => (
                <option key={tax.guid} value={tax.guid}>
                  {tax.percentage}
                </option>
              ))}
            </select>
          </td>
        ) : null}
        <td className="pos-num pos-net">{fmt(t.net)}</td>
        <td>
          <div className="pos-rowactions">
            <button
              type="button"
              className={`pos-iconbtn ${line.notes ? "is-set" : ""}`.trim()}
              onClick={() => {
                setNoteLine(line.key);
                setDialog("note");
              }}
              title="Item notes"
              aria-label={`Notes for ${line.name}`}
            >
              <FileText />
            </button>
            <button
              type="button"
              className="pos-iconbtn pos-iconbtn--danger"
              onClick={() => removeLine(line.key)}
              title="Remove"
              aria-label={`Remove ${line.name}`}
            >
              <Trash2 />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const padKey = (label, token, className = "", aria) => (
    <button
      type="button"
      tabIndex={-1}
      className={`pos-key ${className}`.trim()}
      onMouseDown={keepFocus}
      onClick={() => pressKey(token)}
      aria-label={aria || (typeof label === "string" ? label : token)}
    >
      {label}
    </button>
  );

  const jumpKey = (label, kind) => (
    <button
      type="button"
      tabIndex={-1}
      className={`pos-key pos-key--fn ${fieldIs(kind) ? "is-on" : ""}`.trim()}
      onMouseDown={keepFocus}
      onClick={() => focusLineField(kind)}
    >
      {label}
    </button>
  );

  const noteTarget = lines.find((line) => line.key === noteLine);
  const customerName = customer?.label || "Walk-in customer";
  const customerSub = customer
    ? mobile || customer?.customer?.mobile || "No mobile on file"
    : mobile || "Tap to attach a customer";

  return (
    <div
      className="pos-screen"
      ref={rootRef}
      onFocus={handleFocusIn}
      onKeyDown={containEnter}
      onClick={refocusOnNeutralTap}
    >
      {/* ================= status strip ================= */}
      <header className="pos-strip">
        <div className="pos-brand">
          <ShoppingCart />
          POS Billing
        </div>
        <span className="pos-strip__sep" />
        <span className={`pos-strip__dot ${online ? "" : "is-off"}`.trim()} />
        <span>{online ? "Online" : "Offline"}</span>
        <span className="pos-strip__sep" />
        <span className="pos-strip__item pos-strip__item--store">
          <span className="pos-strip__k">Store</span>
          {companyInfolist?.[0]?.name ? `${companyInfolist[0].name} · ` : ""}
          {branch?.name || "—"}
        </span>
        <span className="pos-strip__sep" />
        <span className="pos-strip__item pos-strip__item--cashier">
          <User />
          <span className="pos-strip__k">Cashier</span>
          {cashierName}
        </span>
        <span className="pos-strip__sep pos-strip__wide" />
        <button
          type="button"
          className="pos-strip__item pos-strip__wide pos-strip__counter"
          onClick={() => pickCounter(posState)}
          title="This till's counter - tap to change"
        >
          <span className="pos-strip__k">{tillCounter?.counterName || "No counter"}</span>
          {tillCounter ? <span className="pos-num">Shift {tillCounter.shiftNo}</span> : null}
        </button>

        <span className="pos-strip__spacer" />

        <span className="pos-strip__k pos-num">{clock.format("DD MMM YYYY, hh:mm A")}</span>
        <span className="pos-strip__txn" title="Business date — it moves on after day close">
          <Lock />
          <span className="pos-strip__k">Transaction date</span>
          <span className="pos-num">{businessDate.format("DD MMM YYYY")}</span>
        </span>
        <button type="button" className="pos-tillbtn" onClick={() => setDialog("keys")} aria-label="Shortcuts">
          <HelpCircle />
        </button>
        <button type="button" className="pos-tillbtn" data-pop-anchor onClick={openPopover("settings", true)}>
          <Settings />
          Settings
        </button>
        <button type="button" className="pos-tillbtn" onClick={() => onExit("close")}>
          <LogOut />
          Exit
        </button>
      </header>

      {/* ================= stage ================= */}
      <div className="pos-stage">
        {/* ---------------- left : the bill ---------------- */}
        <div className="pos-left">
          {/* product entry, customer and bill details on one line */}
          <section className="pos-card pos-scanrow">
            <div className="pos-entry">
              <div className={`pos-scanbox ${fieldIs("scan") ? "is-focus" : ""}`.trim()}>
                <span className="pos-scanbox__dot" title="Ready to scan" />
                <input
                  ref={scanRef}
                  type="text"
                  inputMode={im}
                  autoComplete="off"
                  data-pos-field="scan"
                  value={scan}
                  onChange={(event) => setScan(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleScan(event.currentTarget.value);
                    } else if (event.key === "Escape") {
                      setScan("");
                    }
                  }}
                  placeholder="Scan barcode, or type a name or code"
                  aria-label="Product entry"
                />
                <span className="pos-scanbox__hint">F3 search · Esc clear</span>
                <button
                  type="button"
                  className="pos-scanbox__btn"
                  onClick={() => {
                    setLookupTerm(scan);
                    setDialog("lookup");
                  }}
                  aria-label="Search items by name"
                >
                  <Search />
                </button>
              </div>

              {/* a quiet confirmation of what the last scan rang up */}
              <div className="pos-lastscan" aria-live="polite">
                {lastScanLine ? (
                  <>
                    <span className="pos-lastscan__k">Last scanned</span>
                    <b>{lastScanLine.name}</b>
                    <span className="pos-num">
                      {num(lastScanLine.quantity)} × {fmt(lastScanLine.price)} = {fmt(lineTotals(lastScanLine).net)}
                    </span>
                  </>
                ) : (
                  <span className="pos-lastscan__k">The last item scanned shows here</span>
                )}
              </div>
            </div>

            <button type="button" className="pos-cust" onClick={() => setDialog("customer")}>
              <span className="pos-cust__icon">
                <User />
              </span>
              <span className="pos-cust__text">
                <b>{customerName}</b>
                <span>{customerSub}</span>
              </span>
            </button>

            <button
              type="button"
              className={`pos-kebab ${panelOpen ? "is-on" : ""}`.trim()}
              data-panel-anchor
              onClick={() => setPanelOpen((prev) => !prev)}
              aria-label="Warehouse, salesman and remarks"
              aria-expanded={panelOpen}
            >
              <MoreVertical />
              {salesman || remarks ? <span className="pos-kebab__dot" /> : null}
            </button>

            {panelOpen ? (
              <div className="pos-panel" role="dialog" aria-label="Bill details">
                <div className="pos-panel__head">Bill details</div>
                <POSField icon={<Home />} label="Warehouse">
                  <Select
                    className="pos-select"
                    classNamePrefix="pos-select"
                    options={warehouseOptions}
                    value={warehouse}
                    placeholder="Select warehouse"
                    isSearchable={false}
                    onChange={setWarehouse}
                  />
                </POSField>
                <POSField icon={<UserCheck />} label="Salesman">
                  <Select
                    className="pos-select"
                    classNamePrefix="pos-select"
                    options={salesmanOptions}
                    value={salesman}
                    isClearable
                    isSearchable={false}
                    placeholder="Select salesman"
                    onChange={setSalesman}
                  />
                </POSField>
                <POSField icon={<FileText />} label="Remarks">
                  <button
                    type="button"
                    className="pos-panel__remarks"
                    onClick={() => {
                      setPanelOpen(false);
                      setDialog("remarks");
                    }}
                  >
                    {remarks || <span>Tap to add remarks</span>}
                  </button>
                </POSField>
                <button type="button" className="pos-btn pos-btn--primary pos-panel__done" onClick={() => {
                  setPanelOpen(false);
                  focusScan();
                }}>
                  Done
                </button>
              </div>
            ) : null}
          </section>

          {/* ---------------- lines ---------------- */}
          <section className="pos-card pos-items">
            <div className="pos-items__head">
              <h2>Bill lines</h2>
              <span className="pos-count">
                {lines.length} item{lines.length === 1 ? "" : "s"} · {unitsLabel} unit{totals.units === 1 ? "" : "s"}
              </span>
              <button type="button" className="pos-linkbtn pos-items__add" onClick={() => setDialog("manual")}>
                <Plus />
                Add manual item
              </button>
            </div>

            <div className="pos-items__body">
              <table className="pos-table">
                <colgroup>
                  <col className="c-idx" />
                  <col />
                  <col className="c-qty" />
                  <col className="c-price" />
                  <col className="c-disc" />
                  {showTax ? <col className="c-tax" /> : null}
                  <col className="c-net" />
                  <col className="c-act" />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th className="pos-mid">Qty</th>
                    <th className="pos-num">Price</th>
                    <th className="pos-num">Disc %</th>
                    {showTax ? <th className="pos-num">Tax %</th> : null}
                    <th className="pos-num">Net Amount</th>
                    <th className="pos-mid">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(renderLineRow)}
                  <tr className="pos-row--new">
                    <td className="pos-idx">{lines.length + 1}</td>
                    <td colSpan={showTax ? 7 : 6}>
                      <Select
                        className="pos-select pos-select--row"
                        classNamePrefix="pos-select"
                        options={productOptions}
                        value={null}
                        placeholder="Select or search product…"
                        filterOption={productFilter}
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        styles={portalStyles}
                        onChange={(option) => {
                          if (option) pickProduct(option.product);
                          focusScan();
                        }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              {!lines.length ? (
                <div className="pos-empty">
                  <b>No items yet</b>
                  Scan a barcode, type a code on the keypad and press Enter, or tap Quick Item.
                </div>
              ) : null}
            </div>
          </section>

          {/* ---------------- function tiles ---------------- */}
          <section className="pos-tiles">
            <button type="button" className="pos-tile" onClick={() => setDialog("manual")}>
              <span className="pos-tile__icon">
                <Tag />
              </span>
              <span className="pos-tile__label">Non Barcode Item</span>
            </button>
            <button type="button" className="pos-tile" onClick={() => setDialog("lookup")}>
              <span className="pos-tile__icon">
                <Search />
              </span>
              <span className="pos-tile__label">Item Look Up</span>
            </button>
            <button type="button" className="pos-tile" onClick={() => setDialog("quick")}>
              <span className="pos-tile__icon">
                <Grid />
              </span>
              <span className="pos-tile__label">Quick Item</span>
            </button>
            <button type="button" className="pos-tile" onClick={() => setDialog("stock")}>
              <span className="pos-tile__icon">
                <Package />
              </span>
              <span className="pos-tile__label">Stock &amp; Price Check</span>
            </button>
            <button type="button" className="pos-tile pos-tile--value" data-pop-anchor onClick={openPopover("charge")}>
              <span className="pos-tile__label">Extra Charge</span>
              <span className="pos-tile__value pos-num">{fmt(extraCharge)}</span>
            </button>
            <button type="button" className="pos-tile pos-tile--value" data-pop-anchor onClick={openPopover("discount")}>
              <span className="pos-tile__label">Discount</span>
              <span className="pos-tile__value pos-num">{fmt(billDiscount)}</span>
            </button>
            <button type="button" className="pos-tile pos-tile--value" data-pop-anchor onClick={openPopover("round")}>
              <span className="pos-tile__label">Round Off</span>
              <span className="pos-tile__value pos-num">{fmt(-num(roundOff))}</span>
            </button>
          </section>

          {/* ---------------- bottom bar ---------------- */}
          <section className="pos-bottom">
            <button type="button" className="pos-bbtn" onClick={() => onExit("salesReturn")}>
              <CornerUpLeft />
              Sales Return
            </button>
            <button type="button" className="pos-bbtn" onClick={holdBill}>
              <Pause />
              Hold Bill
            </button>
            <button type="button" className="pos-bbtn" onClick={() => setDialog("held")}>
              <RotateCcw />
              Recall
              {heldBills.length ? <span className="pos-bbtn__n">{heldBills.length}</span> : null}
            </button>
            <button type="button" className="pos-bbtn" onClick={openRecentBills}>
              <Clock />
              Recent Bills
            </button>
            <button type="button" className="pos-bbtn" onClick={() => setDialog("remarks")}>
              <FileText />
              Remarks
              {remarks ? <span className="pos-bbtn__n pos-bbtn__n--accent">1</span> : null}
            </button>
            <button type="button" className="pos-bbtn pos-bbtn--danger" onClick={confirmReset}>
              <X />
              Reset
            </button>
          </section>
        </div>

        {/* ---------------- right : payment rail (never scrolls) ----------------
            A div, not an <aside>: the POS template styles every aside with
            24px padding, a white ground and height:100% (_pos-design.scss). */}
        <div className="pos-right">
          <section className="pos-card pos-bill">
            <div className="pos-bill__head">
              <b>Current bill</b>
              <span className="pos-mono">#{entryNumber || "—"}</span>
            </div>
            <div className="pos-sums">
              <SumLine label="Subtotal" value={fmt(totals.gross)} />
              <SumLine label="Line discounts" value={totals.lineDiscount ? `−${fmt(totals.lineDiscount)}` : fmt(0)} tone="neg" />
              {showTax ? <SumLine label={taxLabel} value={fmt(totals.tax)} /> : null}
              <SumLine label="Round off" value={fmt(-num(roundOff))} />
              {num(extraCharge) ? <SumLine label="Extra charge" value={fmt(extraCharge)} /> : null}
              {num(billDiscount) ? <SumLine label="Bill discount" value={`−${fmt(billDiscount)}`} tone="neg" /> : null}
            </div>
            <div className="pos-hero">
              <span className="pos-hero__l">Total due</span>
              <span className="pos-hero__v pos-num">{fmt(totals.net)}</span>
            </div>
          </section>

          <section className="pos-card pos-keys" data-keep-pop>
            <div className="pos-tender">
              <label className={`pos-amt ${fieldIs("paid") ? "is-target" : ""}`.trim()}>
                <span className="pos-amt__l">Paid</span>
                <input
                  ref={paidRef}
                  {...numericProps("paid")}
                  className="pos-amt__v pos-num"
                  value={isCreditOnly ? fmt(totals.net) : splitPay ? fmt(splitTotal) : paid}
                  disabled={splitPay || isCreditOnly}
                  placeholder="0.00"
                  onChange={(event) => setPaid(event.target.value)}
                  onKeyDown={enterToScan}
                />
              </label>
              <div className={`pos-amt ${change >= 0 ? "pos-amt--change" : "pos-amt--short"}`}>
                <span className="pos-amt__l">{change >= 0 ? "Change" : "Short by"}</span>
                <span className="pos-amt__v pos-num">{fmt(Math.abs(change))}</span>
              </div>
            </div>

            {/* four rows, not five: on a 768px panel inside a browser the
                fifth row is what got cut off */}
            <div className="pos-pad">
              {padKey("7", "7")}
              {padKey("8", "8")}
              {padKey("9", "9")}
              {padKey(<Delete />, "back", "pos-key--fn", "Backspace")}
              {jumpKey("Qty", "qty")}
              {padKey("4", "4")}
              {padKey("5", "5")}
              {padKey("6", "6")}
              {padKey("C", "clear", "pos-key--fn", "Clear")}
              {jumpKey("Price", "price")}
              {padKey("1", "1")}
              {padKey("2", "2")}
              {padKey("3", "3")}
              {padKey(".", ".")}
              {jumpKey("Disc %", "disc")}
              {padKey("0", "0")}
              {padKey("00", "00")}
              {padKey(
                <>
                  <CornerDownLeft /> Enter
                </>,
                "enter",
                "pos-key--enter pos-key--span3",
                "Enter"
              )}
            </div>
          </section>

          <section className="pos-card pos-pay">
            <div className="pos-pay__head">
              <CreditCard />
              <b>Payment</b>
              <button
                type="button"
                className={`pos-switch ${splitPay ? "is-on" : ""}`.trim()}
                aria-pressed={splitPay}
                onClick={() => {
                  setSplitPay((prev) => !prev);
                  setPayments(ZERO_PAYMENTS);
                  setPaid("");
                }}
              >
                Split payment
                <span className="pos-switch__track" />
              </button>
            </div>

            {splitPay ? (
              <div className="pos-split">
                {PAY_MODES.map((mode) => (
                  <div key={mode.key} className={`pos-splitin ${fieldIs(`pay-${mode.key}`) ? "is-target" : ""}`.trim()}>
                    <button
                      type="button"
                      className="pos-splitin__m"
                      onClick={() => fillRemaining(mode.key)}
                      title="Fill with the amount still due"
                    >
                      {mode.icon}
                      {mode.label}
                    </button>
                    <input
                      {...numericProps(`pay-${mode.key}`)}
                      className="pos-num"
                      value={payments[mode.key]}
                      placeholder="0.00"
                      onChange={(event) => setPayments((prev) => ({ ...prev, [mode.key]: event.target.value }))}
                      onKeyDown={enterToScan}
                      aria-label={`${mode.label} amount`}
                    />
                    <button
                      type="button"
                      className="pos-splitin__x"
                      tabIndex={-1}
                      onMouseDown={keepFocus}
                      onClick={() => setPayments((prev) => ({ ...prev, [mode.key]: "" }))}
                      aria-label={`Clear ${mode.label}`}
                    >
                      <X />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pos-methods">
                {PAY_MODES.map((mode) => (
                  <button
                    key={mode.key}
                    type="button"
                    className="pos-mth"
                    aria-pressed={payMode === mode.value}
                    onClick={() => setPayMode(mode.value)}
                  >
                    {mode.icon}
                    {mode.label}
                  </button>
                ))}
              </div>
            )}

            <div className="pos-quick">
              <span className="pos-quick__l">Quick cash</span>
              {quickCash.length ? (
                quickCash.map((value, index) => (
                  <button
                    key={value}
                    type="button"
                    className={`pos-chip ${index === 0 ? "pos-chip--exact" : ""}`.trim()}
                    disabled={isCreditOnly}
                    onClick={() => applyQuickCash(value)}
                  >
                    {index === 0 ? "Exact" : value}
                  </button>
                ))
              ) : (
                <span className="pos-quick__none">Add items to see tender amounts</span>
              )}
            </div>

            <div className="pos-actions">
              <button type="button" className="pos-save" disabled={saving} onClick={() => saveBill(false)}>
                <Save />
                SAVE
                <small>F9</small>
              </button>
              <button type="button" className="pos-save pos-save--go" disabled={saving} onClick={() => saveBill(true)}>
                <Printer />
                SAVE &amp; PRINT
                <small>F10</small>
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* ================= popovers ================= */}
      {popover?.name === "charge" ? (
        <div className="pos-pop" style={popStyle}>
          <h6>Extra charge</h6>
          <div className="pos-pop__row">
            <input
              {...numericProps("charge")}
              value={extraCharge}
              placeholder="0.00"
              onChange={(event) => setExtraCharge(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && closePopover()}
            />
            <button type="button" className="pos-btn" onClick={closePopover}>
              Done
            </button>
          </div>
          <p className="pos-hint">Delivery, packing or any other charge. Type it on the keypad.</p>
        </div>
      ) : null}

      {popover?.name === "discount" ? (
        <div className="pos-pop" style={popStyle}>
          <h6>Bill discount</h6>
          <div className="pos-pop__row">
            <input
              {...numericProps("discount")}
              value={billDiscount}
              placeholder="0.00"
              onChange={(event) => setBillDiscount(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && closePopover()}
            />
            <button type="button" className="pos-btn" onClick={closePopover}>
              Done
            </button>
          </div>
          <div className="pos-pop__seg">
            {[5, 10, 15].map((percent) => (
              <button
                key={percent}
                type="button"
                onClick={() => setBillDiscount((((totals.net + num(billDiscount)) * percent) / 100).toFixed(decimals))}
              >
                {percent}%
              </button>
            ))}
            <button type="button" onClick={() => setBillDiscount("")}>
              Clear
            </button>
          </div>
          <p className="pos-hint">An amount taken off after line discounts and tax.</p>
        </div>
      ) : null}

      {popover?.name === "round" ? (
        <div className="pos-pop" style={popStyle}>
          <h6>Round off</h6>
          <div className="pos-pop__row">
            <input
              {...numericProps("round")}
              value={roundOff}
              placeholder="0.00"
              onChange={(event) => setRoundOff(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && closePopover()}
            />
            <button
              type="button"
              className="pos-btn"
              onClick={() => {
                // The figure that lands the total on a whole unit.
                const before = totals.net + num(roundOff);
                setRoundOff((before - Math.round(before)).toFixed(decimals));
              }}
            >
              Auto
            </button>
          </div>
          <p className="pos-hint">A positive figure reduces the total due.</p>
        </div>
      ) : null}

      {popover?.name === "settings" ? (
        <div className="pos-pop" style={{ ...popStyle, left: Math.min(popover.left, window.innerWidth - 300) }}>
          <h6>Terminal settings</h6>
          <label className="pos-pop__check">
            <input type="checkbox" checked={prefs.printOnSave} onChange={(event) => updatePref("printOnSave", event.target.checked)} />
            Print a receipt on every save
          </label>
          <label className="pos-pop__check">
            <input type="checkbox" checked={prefs.touchMode} onChange={(event) => updatePref("touchMode", event.target.checked)} />
            Touch terminal — use the on-screen keys, keep the device keyboard hidden
          </label>
          <p className="pos-hint">Saved on this device. Tax columns follow the branch setting.</p>
        </div>
      ) : null}

      {/* ================= dialogs ================= */}
      {dialog === "lookup" || dialog === "quick" ? (
        <ItemLookupDialog
          products={productList}
          decimals={decimals}
          quick={dialog === "quick"}
          initialTerm={dialog === "lookup" ? lookupTerm : ""}
          touchMode={prefs.touchMode}
          onPick={(product) => {
            if (dialog === "lookup") closeDialog();
            pickProduct(product);
          }}
          onClose={closeDialog}
        />
      ) : null}

      {dialog === "stock" ? (
        <StockPriceCheckDialog
          products={productList}
          taxOf={taxOf}
          decimals={decimals}
          touchMode={prefs.touchMode}
          onAdd={(product) => {
            closeDialog();
            pickProduct(product);
          }}
          onClose={closeDialog}
        />
      ) : null}

      {variantPick ? (
        <VariantPickDialog
          product={variantPick.product}
          branchID={branch?.guid}
          decimals={decimals}
          onPick={(product) => {
            addProduct(product, variantPick.quantity);
            setVariantPick(null);
            if (!dialog) focusScan();
          }}
          onPlain={() => {
            addProduct(variantPick.product, variantPick.quantity);
            setVariantPick(null);
            if (!dialog) focusScan();
          }}
          onClose={() => {
            setVariantPick(null);
            if (!dialog) focusScan();
          }}
        />
      ) : null}

      {dialog === "manual" ? (
        <ManualItemDialog
          products={productOptions}
          taxOptions={taxList || []}
          touchMode={prefs.touchMode}
          onSave={(item) => {
            const line = { ...emptyLine(), ...item, manual: true };
            setLines((prev) => [...prev, line]);
            setActiveKey(line.key);
            setLastScanKey(line.key);
            closeDialog();
          }}
          onClose={closeDialog}
        />
      ) : null}

      {dialog === "customer" ? (
        <CustomerDialog
          customers={customerOptions}
          value={customer}
          mobile={mobile}
          touchMode={prefs.touchMode}
          onPick={(option, phone) => {
            setCustomer(option);
            setMobile(phone || "");
            closeDialog();
          }}
          onAddNew={() => {
            setDialog(null);
            setCustomerModal(true);
          }}
          onClose={closeDialog}
        />
      ) : null}

      {dialog === "note" && noteTarget ? (
        <TextEntryDialog
          title="Item notes"
          subtitle={noteTarget.name}
          initial={noteTarget.notes}
          placeholder="Notes for this item — printed under it on the receipt"
          saveLabel="Save note"
          touchMode={prefs.touchMode}
          onSave={(text) => {
            updateLine(noteTarget.key, { notes: text });
            closeDialog();
          }}
          onClose={closeDialog}
        />
      ) : null}

      {dialog === "remarks" ? (
        <TextEntryDialog
          title="Remarks"
          subtitle="Saved with the bill and printed on the receipt"
          initial={remarks}
          placeholder="Remarks for this bill"
          saveLabel="Save remarks"
          touchMode={prefs.touchMode}
          onSave={(text) => {
            setRemarks(text);
            closeDialog();
          }}
          onClose={closeDialog}
        />
      ) : null}

      {dialog === "recent" ? (
        <RecentBillsDialog
          bills={recentBills}
          loading={recentLoading}
          decimals={decimals}
          onPrint={printSavedBill}
          onClose={closeDialog}
        />
      ) : null}

      {dialog === "held" ? (
        <HeldBillsDialog
          bills={heldBills}
          decimals={decimals}
          onRecall={recallBill}
          onDelete={deleteHeldBill}
          onClose={closeDialog}
        />
      ) : null}

      {dialog === "keys" ? <ShortcutsDialog onClose={closeDialog} /> : null}

      {customerModal ? (
        <CustomerModal
          mode="add"
          data={{}}
          handleClose={() => {
            setCustomerModal(false);
            focusScan();
          }}
          handleRefresh={() => dispatch(fetchCustomer(branch))}
        />
      ) : null}
    </div>
  );
};

POSBillingScreen.propTypes = {
  onExit: PropTypes.func.isRequired,
};

export default POSBillingScreen;
